import type {
  AttributionCookieDto as AttributionCookie,
  AttributionTouchDto as Touch,
} from "@repo/api/attribution";
import { camelCaseKeys } from "@repo/api/utils/camel-case";

const AMPLIFILES_DOMAIN = "amplifiles.ai";
const COOKIE_NAME = "amplifiles_attribution";
const COOKIE_MAX_AGE_DAYS = 90;

const UTM_KEYS = [
  "utmSource",
  "utmMedium",
  "utmCampaign",
  "utmTerm",
  "utmContent",
  "utmId",
] as const;
const CLICK_ID_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "ttclid",
  "twclid",
  "rdtCid",
  "liFatId",
  "ScCid",
  "epik",
  "vmcid",
  "oppref",
] as const;

function readRawCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^|; )" + name + "=([^;]*)"));
  return match?.[2] || null;
}

function readCookie(name: string): string | null {
  const raw = readRawCookie(name);
  if (!raw) return null;
  try {
    return atob(raw);
  } catch {
    return null;
  }
}

export function loadExistingAttribution(): AttributionCookie | null {
  try {
    const raw = readCookie(COOKIE_NAME);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return camelCaseKeys(parsed) as AttributionCookie;
  } catch {
    return null;
  }
}

function parseQuery(): Record<string, string> {
  const out: Record<string, string> = {};
  const q = new URLSearchParams(window.location.search);
  q.forEach((v, k) => {
    if (v != null && v !== "") out[k] = v;
  });
  return out;
}

function parseHost(url?: string): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname || null;
  } catch {
    return null;
  }
}

function parsePath(url?: string): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.pathname;
  } catch {
    return null;
  }
}

function hasAny(obj: Record<string, unknown> | undefined | null): boolean {
  if (!obj) return false;

  const values = Object.values(obj);

  return values.some((v) => !!v);
}

function endsWithOneOf(
  host: string | undefined | null,
  suffixes: string[],
): boolean {
  if (!host) return false;

  for (const s of suffixes) {
    if (host === s || host.endsWith("." + s)) return true;
  }
  return false;
}

function isInvitePath(path: string | null): boolean {
  if (!path) return false;
  return /^\/i\/[^/]+/.test(path);
}

function deriveSourceMedium(
  utm: Touch["utm"],
  clickIds: Touch["clickIds"],
  refHost: string | null,
): { source?: string; medium?: string } {
  // If UTM present, prefer it.
  if (utm.utmSource) {
    return {
      source: utm.utmSource,
      medium: utm.utmMedium || (hasAny(clickIds) ? "paid" : "referral"),
    };
  }

  // Click IDs → infer ad network
  if (clickIds.gclid || clickIds.gbraid || clickIds.wbraid)
    return { source: "google_ads", medium: "paid" };
  if (clickIds.msclkid) return { source: "microsoft_ads", medium: "paid" };
  if (clickIds.fbclid) return { source: "facebook_ads", medium: "paid" };
  if (clickIds.ttclid) return { source: "tiktok_ads", medium: "paid" };
  if (clickIds.twclid) return { source: "twitter_ads", medium: "paid" };
  if (clickIds.rdtCid) return { source: "reddit_ads", medium: "paid" };
  if (clickIds.liFatId) return { source: "linkedin_ads", medium: "paid" };
  if (clickIds.ScCid) return { source: "snapchat_ads", medium: "paid" };
  if (clickIds.epik) return { source: "pinterest_ads", medium: "paid" };
  if (clickIds.vmcid) return { source: "yahoo_ads", medium: "paid" };
  if (clickIds.oppref) return { source: "openai_ads", medium: "paid" };

  if (refHost) {
    const searchEngines = [
      "google.com",
      "bing.com",
      "duckduckgo.com",
      "yahoo.com",
      "yandex.ru",
      "baidu.com",
      "ecosia.org",
    ];
    const social = [
      "facebook.com",
      "instagram.com",
      "tiktok.com",
      "twitter.com",
      "x.com",
      "linkedin.com",
      "youtube.com",
      "reddit.com",
    ];
    if (endsWithOneOf(refHost, searchEngines))
      return { source: "organic_search", medium: "organic" };
    if (endsWithOneOf(refHost, social))
      return { source: "social", medium: "social" };
    return { source: refHost, medium: "referral" };
  }

  return {}; // direct
}

function buildTouch(): Touch {
  const params = camelCaseKeys(parseQuery());

  const utm: Touch["utm"] = {};
  for (const k of UTM_KEYS) {
    if (params[k]) utm[k] = params[k];
  }

  const clickIds: Touch["clickIds"] = {};
  for (const k of CLICK_ID_KEYS) {
    if (params[k]) clickIds[k] = params[k];
  }

  const refHost = parseHost(document?.referrer);
  const derived = deriveSourceMedium(utm, clickIds, refHost);

  return {
    ts: Date.now(),
    rawReferrer: document?.referrer,
    rawLocation: location?.href,
    host: parseHost(location?.href),
    page: parsePath(location?.href),
    referrerHost: parseHost(document?.referrer),
    referrerPath: parsePath(document?.referrer),
    utm,
    clickIds,
    source: derived.source || null,
    medium: derived.medium || null,
    userAgent: navigator.userAgent || undefined,
  };
}

export function enrichFacebookClickIds(cookie: AttributionCookie) {
  try {
    if (!cookie.firstTouch.clickIds?.fbclid) return;

    const fbc = readRawCookie("_fbc");
    const fbp = readRawCookie("_fbp");

    if (fbc) cookie.firstTouch.clickIds.fbc = fbc;
    if (fbp) cookie.firstTouch.clickIds.fbp = fbp;
  } catch {
    console.error("Error parsing attribution cookie");
  }
}

function writeCookie(name: string, value: string, days: number) {
  if (typeof document === "undefined") return;
  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000,
  ).toUTCString();
  let cookie = `${name}=${btoa(value)}; Expires=${expires}; Path=/; SameSite=Lax`;
  const isSecure =
    typeof location !== "undefined" && location.protocol === "https:";
  if (isSecure) cookie += "; Secure";
  cookie += `; Domain=.${AMPLIFILES_DOMAIN}`;
  document.cookie = cookie;
}

export function storeAmplifilesAttribution(shouldWriteCookie: boolean = true) {
  const existing = loadExistingAttribution();
  const currentTouch = buildTouch();

  const hasSignal =
    hasAny(currentTouch.utm) ||
    hasAny(currentTouch.clickIds) ||
    !!currentTouch.referrerHost;

  const isAmplifilesReferrer = endsWithOneOf(parseHost(document?.referrer), [
    AMPLIFILES_DOMAIN,
  ]);

  const isIgnoredReferrer = endsWithOneOf(parseHost(document?.referrer), [
    "accounts.google.com",
    "account.live.com",
  ]);

  // If referrer path is /i/<slug>, we should do tracking even if it's from Amplifiles domain
  const isInviteReferrer = isInvitePath(currentTouch.referrerPath);

  if (
    !hasSignal ||
    (isAmplifilesReferrer && !isInviteReferrer) ||
    isIgnoredReferrer
  ) {
    (window as any).amplifilesAttribution = existing;
    return;
  }

  const cookie: AttributionCookie = existing ?? {
    firstTouch: currentTouch,
    lastTouch: currentTouch,
  };

  cookie.lastTouch = currentTouch;

  if (shouldWriteCookie) {
    writeCookie(COOKIE_NAME, JSON.stringify(cookie), COOKIE_MAX_AGE_DAYS);
  }

  (window as any).amplifilesAttribution = cookie;
}

if (typeof window !== "undefined") {
  (window as any).storeAmplifilesAttribution = storeAmplifilesAttribution;
}
